import React, { useMemo, useState } from 'react';
import { AlertTriangle, Shield } from 'lucide-react';
import { InfoBadge } from '../../../../components/UI/InfoBadge';
import LoadingSpinner from '../../../../components/UI/LoadingSpinner';
import { Pagination } from '../../../../components/UI/Pagination';
import PillToggle from '../../../../components/UI/PillToggle';
import { StatusBadge } from '../../../../components/UI/StatusBadge';
import { TradeBadge } from '../../../../components/UI/TradeBadge';
import { useCourseRequestBondManagement } from '../../../../hooks/useCourseRequests';
import {
  getDepartmentBadgeColor,
  getDepartmentShortName,
} from '../../../../config/departmentConfig';
import { isTradeRedundantWithDepartment } from '../../../../config/tradeConfig';
import { formatDate } from '../../../../utils/dateUtils';
import { formatCurrency } from '../../../../utils/numberUtils';

const PAGE_SIZE = 10;

const BondWorkerIdentity = ({ item }) => {
  const showTrade = (item.trade_code || item.trade_name)
    && !isTradeRedundantWithDepartment(
      item.department_name,
      item.trade_code,
      item.trade_name,
    );

  if (!item.department_name && !showTrade) return null;

  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
      {item.department_name && (
        <InfoBadge
          color={getDepartmentBadgeColor(item.department_name)}
          label={getDepartmentShortName(item.department_name)}
          title={item.department_name}
          size="xs"
        />
      )}
      {showTrade && (
        <TradeBadge code={item.trade_code} name={item.trade_name} size="xs" />
      )}
    </div>
  );
};

const ConfirmedBondCard = ({ item }) => (
  <div className="bg-white rounded-2xl border border-gray-200 p-4">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-gray-900 truncate">
          {item.applicant_name || item.applicant_staff_id || 'Unknown applicant'}
        </p>
        {item.applicant_name && item.applicant_staff_id && (
          <p className="text-xs text-gray-500 mt-0.5">{item.applicant_staff_id}</p>
        )}
        <BondWorkerIdentity item={item} />
      </div>
      <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
        <StatusBadge
          status={item.bond_status || 'pending_confirmation'}
          label={item.bond_status ? undefined : 'Agreement not recorded'}
        />
        {item.result && <StatusBadge status={item.result} />}
      </div>
    </div>

    <div className="mt-3">
      <p className="field-label">Sponsored course</p>
      <p className="text-sm font-medium text-gray-900 mt-0.5 line-clamp-2">
        {item.booked_course_name || item.display_course_name}
      </p>
    </div>

    <dl className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
      <div>
        <dt className="field-label">Bond term</dt>
        <dd className="field-display mt-0.5">
          {item.bond_duration_months != null
            ? `${item.bond_duration_months} months`
            : '—'}
        </dd>
      </div>
      <div>
        <dt className="field-label">Sponsored amount</dt>
        <dd className="field-display mt-0.5">
          {item.course_amount != null && item.course_amount !== ''
            ? formatCurrency(item.course_amount)
            : '—'}
        </dd>
      </div>
      <div className="col-span-2 sm:col-span-1">
        <dt className="field-label">Course dates</dt>
        <dd className="field-display mt-0.5">
          {item.course_start_date ? (
            <>
              {formatDate(item.course_start_date)}
              {item.course_end_date && item.course_end_date !== item.course_start_date
                ? ` – ${formatDate(item.course_end_date)}`
                : ''}
            </>
          ) : '—'}
        </dd>
      </div>
    </dl>

    <div className="mt-3 pt-2 border-t border-gray-100 text-xs text-gray-400">
      {item.request_number}
    </div>
  </div>
);

const ReviewCandidateCard = ({ item }) => (
  <div className="bg-white rounded-2xl border border-gray-200 p-4">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-gray-900 truncate">
          {item.staff_name || item.staff_id || 'Unknown employee'}
        </p>
        {item.staff_name && item.staff_id && (
          <p className="text-xs text-gray-500 mt-0.5">{item.staff_id}</p>
        )}
        <BondWorkerIdentity item={item} />
      </div>
      <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
        <StatusBadge status="pending_hr" label="Needs bond review" />
        <StatusBadge status={item.training_status} />
      </div>
    </div>

    <div className="mt-3">
      <p className="field-label">Training course</p>
      <p className="text-sm font-medium text-gray-900 mt-0.5 line-clamp-2">
        {item.course_title}
      </p>
    </div>

    <dl className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
      <div>
        <dt className="field-label">Course fee</dt>
        <dd className="field-display mt-0.5">{formatCurrency(item.course_fee)}</dd>
      </div>
      <div>
        <dt className="field-label">Indicative term</dt>
        <dd className="field-display mt-0.5">
          {item.recommended_bond_duration_months != null
            ? `${item.recommended_bond_duration_months} months`
            : '—'}
        </dd>
      </div>
      <div className="col-span-2 sm:col-span-1">
        <dt className="field-label">Training dates</dt>
        <dd className="field-display mt-0.5">
          {item.training_start_date ? (
            <>
              {formatDate(item.training_start_date)}
              {item.training_end_date && item.training_end_date !== item.training_start_date
                ? ` – ${formatDate(item.training_end_date)}`
                : ''}
            </>
          ) : '—'}
        </dd>
      </div>
    </dl>

    <div className="mt-3 pt-2 border-t border-gray-100 text-xs text-gray-400">
      Historical training record #{item.id}
    </div>
  </div>
);

/**
 * Consolidates explicit Course Request bonds and historical records requiring review.
 */
const BondManagementTab = () => {
  const [view, setView] = useState('recorded');
  const [page, setPage] = useState(1);
  const { data, isLoading, isError } = useCourseRequestBondManagement();

  const confirmed = data?.confirmed || [];
  const reviewCandidates = data?.review_candidates || [];
  const visibleItems = view === 'recorded' ? confirmed : reviewCandidates;
  const totalPages = Math.max(1, Math.ceil(visibleItems.length / PAGE_SIZE));
  const pageItems = useMemo(
    () => visibleItems.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [page, visibleItems],
  );

  const changeView = (nextView) => {
    setView(nextView);
    setPage(1);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 text-red-300 mx-auto mb-3" />
        <p className="text-sm text-gray-600">Unable to load bond records.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Sponsorship bonds</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Track recorded bonds and reconcile historical training sponsorships.
          </p>
        </div>
        <PillToggle
          value={view}
          onChange={changeView}
          options={[
            { id: 'recorded', label: 'Recorded bonds', count: confirmed.length },
            { id: 'review', label: 'Needs review', count: reviewCandidates.length },
          ]}
          variant="text"
          theme="admin"
          layout="sectionHeader"
          ariaLabel="Bond record scope"
        />
      </div>

      {view === 'review' && (
        <div className="flex gap-2.5 rounded-xl border border-amber-200 bg-amber-50 p-3">
          <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-900">
            These historical records exceed S$500, but only course fee was imported.
            HR must confirm the sponsored amount and whether a bond was signed.
          </p>
        </div>
      )}

      {!visibleItems.length ? (
        <div className="text-center py-12">
          <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {view === 'recorded' ? 'No recorded bonds' : 'No records need review'}
          </h3>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {pageItems.map((item) => (
              view === 'recorded'
                ? <ConfirmedBondCard key={`bond-${item.id}`} item={item} />
                : <ReviewCandidateCard key={`review-${item.id}`} item={item} />
            ))}
          </div>
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
            totalCount={visibleItems.length}
            pageSize={PAGE_SIZE}
            theme="admin"
            noun={view === 'recorded' ? 'bonds' : 'records'}
          />
        </>
      )}
    </div>
  );
};

export default BondManagementTab;
